// What the garment pill says — the pure half.
//
// Split out of GarmentIndicator.jsx for the reason every *IndicatorState
// module here is: the wording IS the feature. This pass persists nothing, so
// the pill is the only thing that tells the user the garment on the avatar is
// a local preview; and a garment can render perfectly while a fifth of it is
// rigidly pinned to one bone, which nobody sees until the character moves. If
// those sentences are only reachable through a mounted React tree, they are
// only reachable through a browser, and they never get asserted.
//
// No React import here, so node:test drives the rules.

/**
 * Above this share of unbound vertices the import is reported as a problem
 * rather than a footnote. Some unbound vertices are normal — a drawcord tip, a
 * metal eyelet floating clear of the body — but a fifth of the mesh means the
 * garment is not on this body.
 */
export const UNBOUND_WARN_PCT = 5

/**
 * @param {object} state  garmentState(): { phase, … }
 * @returns {{ label: string, detail: string|null, tone: 'neutral'|'ok'|'warn'|'danger' }|null}
 *   null when there is nothing to say — no garment was asked for.
 */
export function describeGarmentIndicator(state) {
  if (!state || state.phase === 'idle') return null

  if (state.phase === 'loading') {
    return { label: `garment — loading ${state.name}…`, detail: null, tone: 'neutral' }
  }

  if (state.phase === 'error') {
    return {
      label: `garment — ${state.name} failed`,
      detail: state.error ?? 'unknown error',
      tone: 'danger',
    }
  }

  // ready
  const bits = []
  // Whether this is saved comes FIRST — it is the fact the user is most likely
  // to be wrong about. A garment loaded from its server row IS saved; one read
  // out of the local dev directory will vanish on reload, and saying so is the
  // whole reason this line leads.
  if (!state.persisted) bits.push('local preview, not saved')
  bits.push(`${state.pieces} pattern pieces`)
  if (state.units?.unit && state.units.unit !== 'm') bits.push(`${state.units.unit}→m`)

  let tone = 'ok'
  const problems = []
  if (state.unboundPct > UNBOUND_WARN_PCT) {
    tone = 'warn'
    problems.push(`${state.unboundPct.toFixed(0)}% of vertices bound to no body — they will tear away when the character moves`)
  }
  if (state.fit?.verdict === 'mismatch' || state.fit?.verdict === 'misplaced') {
    tone = 'warn'
    problems.push(state.fit.summary)
  }
  if (state.units?.unit === 'unknown') {
    tone = 'warn'
    problems.push(state.units.reason)
  }
  if (state.appliedFit) bits.push('fit transform applied')

  return {
    label: `garment — ${state.name}`,
    detail: problems.length ? `${bits.join(' · ')} — ${problems.join('; ')}` : bits.join(' · '),
    tone,
  }
}
